import React, { useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  useMediaQuery,
  useTheme,
  Grid,
  Checkbox,
  FormControlLabel,
  TextField,
} from "@mui/material";
import { DropzoneArea } from "mui-file-dropzone";
import { v4 as uuidv4 } from "uuid";
import { getAWSS3Url } from "../api/s3Upload";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import { fetchFromWebApi } from "../utils/fetchFromWebApi";

const FileUploadPopup = ({ open, onClose, data, isFromEditor = false }) => {
  // State management
  const [dropzones, setDropzones] = useState([{ id: uuidv4(), files: [] }]);
  const [noDocumentRequired, setNoDocumentRequired] = useState(false);
  const [comment, setComment] = useState("");
  const [noComment, setNoComment] = useState(false);
  const [fileDownloadUrl, setFileDownloadUrl] = useState([]);
  const [loading, setLoading] = useState(false);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const { clientId } = useParams();

  // Handle file changes in dropzone
  const handleFileChange = async (acceptedFiles, dropzoneId) => {
    if (acceptedFiles.length === 0) return;
    setLoading(true);
    for (const file of acceptedFiles) {
      // Prevent duplicate uploads
      if (fileDownloadUrl.some(f => f.fileName === file.name && f.id === dropzoneId)) continue;
      try {
        const { uploadUrl, downloadUrl } = await getAWSS3Url(file.name);
        const resUpload = await fetch(uploadUrl, {
          method: "PUT",
          headers: { "Content-Type": file.type },
          body: file,
        });
        if (resUpload.ok) {
          setFileDownloadUrl((prevUrls) => [
            ...prevUrls,
            { url: downloadUrl, id: dropzoneId, fileName: file.name },
          ]);
          setDropzones((prevDropzones) =>
            prevDropzones.map((dropzone) =>
              dropzone.id === dropzoneId
                ? { ...dropzone, files: [file] }
                : dropzone
            )
          );
        } else {
          toast.error("Error uploading file: " + resUpload.statusText);
        }
      } catch (error) {
        toast.error("Error uploading file: " + error.message);
      }
    }
    setLoading(false);
  };

  // Handle upload submit
  const handleUpload = async () => {
    setLoading(true);
    try {
      const res = await fetchFromWebApi(
        `HHContract/DocumentSendForApproval`,
        {
          method: "POST",
          body: JSON.stringify({
            id: data.Id,
            FileUrls: fileDownloadUrl,
            Comment: comment,
            UserId: localStorage.getItem("UserId"),
            metaData: data.MetaData,
          }),
        }
      );
      if (res?.ok) {
        toast.success("Document sent successfully.");
        handleClose();
        if (isFromEditor) {
          navigate(`/${clientId}`);
        }
      } else {
        const errorText = await res.text();
        toast.error("Failed to save form: " + errorText);
      }
    } catch (error) {
      toast.error("Error while saving form: " + error.message);
    }
    setLoading(false);
  };

  // Handle dialog close and reset state
  const handleClose = () => {
    setDropzones([{ id: uuidv4(), files: [] }]);
    setFileDownloadUrl([]);
    setComment("");
    setNoComment(false);
    setNoDocumentRequired(false);
    setLoading(false);
    onClose();
  };

  // Handle 'No Document Required' checkbox
  const handleNoDocumentRequiredChange = (e) => {
    const checked = e.target.checked;
    setNoDocumentRequired(checked);
    if (checked) {
      setDropzones([{ id: uuidv4(), files: [] }]);
      setFileDownloadUrl([]);
    }
  };

  // Handle 'No Comment' checkbox
  const handleNoCommentChange = (e) => {
    const checked = e.target.checked;
    setNoComment(checked);
    if (checked) setComment("");
  };

  // Determine if submit button should be disabled
  const isSubmitDisabled = () => {
    if (loading) return true;
    if (noComment && noDocumentRequired) return false;
    if (noComment && dropzones.some((dz) => dz.files.length > 0)) return false;
    if (noDocumentRequired && comment) return false;
    if (comment && dropzones.some((dz) => dz.files.length > 0)) return false;
    return true;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
    >
      <DialogTitle>Upload Additional Documents</DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={noComment}
                  onChange={handleNoCommentChange}
                  color="primary"
                />
              }
              label="No Comment Required"
            />
            <TextField
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              label="Comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              disabled={noComment} // Disable when "No Comment" is checked
              placeholder="Write your comment here..."
            />
          </Grid>
          <Grid item xs={12} sm={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={noDocumentRequired}
                  onChange={handleNoDocumentRequiredChange}
                  color="primary"
                />
              }
              label="No Document Required"
            />
            {dropzones.map((dropzone) => (
              <Grid container spacing={2} key={dropzone.id} alignItems="center">
                <Grid item xs={fullScreen ? 11 : 12}>
                  <DropzoneArea
                    onChange={(files) => handleFileChange(files, dropzone.id)}
                    acceptedFiles={[
                      "image/*",
                      "application/pdf",
                      "application/msword",
                      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                      "application/vnd.ms-excel",
                      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    ]}
                    showPreviews={true}
                    showPreviewsInDropzone={false}
                    useChipsForPreview
                    previewGridProps={{
                      container: { spacing: 1, direction: "row" },
                    }}
                    previewText="Selected files"
                    maxFileSize={5000000}
                    filesLimit={20}
                    disabled={noDocumentRequired || loading}
                  />
                </Grid>
              </Grid>
            ))}
          </Grid>
        </Grid>
        {loading && (
          <div style={{ textAlign: "center", marginTop: 16 }}>
            <span>Uploading...</span>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary" fullWidth={fullScreen} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleUpload}
          color="primary"
          disabled={isSubmitDisabled()}
          fullWidth={fullScreen}
        >
          {loading ? "Submitting..." : "Submit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FileUploadPopup;
